import { useRef, useState } from "react";
import { AlertCircle, FileAudio, FileText, Sparkles, Upload, X } from "lucide-react";
import { cn } from "@/utils/cn";
import { PrimaryButton } from "./visuals";

/**
 * Step 4, "Usar meu roteiro": the firm sends its roteiro (PDF, Word, texto or
 * an audio) and/or writes it. One button reads it and builds the agents; the
 * page then shows what was created and the way to test it.
 *
 * The draft lives in the page, so it survives the build screen and an error.
 */

const ACCEPT = ".pdf,.docx,.txt,audio/*";

/** The agents a plan creates, in flow order. */
export function planAgents(plan) {
  if (!plan) return [];
  const byName = new Map((plan.summary || []).map((r) => [r.name, r.does]));
  return [
    { name: "Recepção", does: byName.get("Recepção") || "Entende o caso" },
    ...plan.specialists.map((s) => ({ name: s.name, does: s.focus || byName.get(s.name) || "" })),
    { name: "Fechamento", does: byName.get("Fechamento") || "Conduz para fechar" },
  ];
}

export default function GuideMode({ draft, onDraft, onBuild, error }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const { text, file } = draft;
  const ready = Boolean(file) || text.trim().length > 0;
  const isAudio = file?.type?.startsWith("audio/");

  const pick = (f) => f && onDraft({ ...draft, file: f });

  return (
    <section className="flex flex-col gap-4 rounded-[20px] border border-black/[.08] bg-white/85 p-5 backdrop-blur-xl dark:border-white/10 dark:bg-[#0e0e14]/60">
      {file ? (
        <div className="flex items-center gap-3 rounded-2xl border border-black/[.08] bg-black/[.02] p-3 dark:border-white/15 dark:bg-white/[.03]">
          <span
            aria-hidden="true"
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
              isAudio ? "bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300" : "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
            )}
          >
            {isAudio ? <FileAudio className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15px] font-medium text-foreground">{file.name}</p>
            <p className="text-[13px] text-muted-foreground">{isAudio ? "A IA transcreve o áudio" : "A IA lê o documento"}</p>
          </div>
          <button
            type="button"
            aria-label={`Remover ${file.name}`}
            onClick={() => onDraft({ ...draft, file: null })}
            className="flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground hover:bg-black/[.05] dark:hover:bg-white/[.06]"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            pick(e.dataTransfer.files?.[0]);
          }}
          className={cn(
            "flex flex-col items-center gap-2 rounded-2xl border border-dashed px-4 py-8 text-center transition-colors",
            dragging ? "border-foreground bg-black/[.03] dark:bg-white/[.05]" : "border-black/[.15] hover:border-foreground/40 dark:border-white/20",
          )}
        >
          <span aria-hidden="true" className="flex h-11 w-11 items-center justify-center rounded-full bg-black/[.05] text-foreground dark:bg-white/10">
            <Upload className="h-5 w-5" />
          </span>
          <span className="text-[15px] font-semibold text-foreground">Envie o roteiro do escritório</span>
          <span className="text-[13px] text-muted-foreground">PDF, Word ou áudio. Arraste aqui ou clique para escolher.</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        aria-label="Anexar roteiro"
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = "";
        }}
      />

      <div>
        <label htmlFor="guide-text" className="mb-2 block text-sm font-medium text-foreground">
          {file ? "Quer explicar algo a mais? (opcional)" : "Ou escreva como o escritório atende"}
        </label>
        <textarea
          id="guide-text"
          rows={file ? 3 : 5}
          value={text}
          onChange={(e) => onDraft({ ...draft, text: e.target.value })}
          placeholder="O que perguntar, para quais casos, o que não fazer."
          className="w-full resize-y rounded-2xl border border-black/[.08] bg-white/70 px-4 py-3 text-[15px] leading-relaxed text-foreground dark:border-white/15 dark:bg-transparent"
        />
      </div>

      {error && (
        <p role="alert" className="flex items-start gap-2 rounded-xl bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:bg-rose-500/10 dark:text-rose-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3 border-t border-black/[.05] pt-4 dark:border-white/[.06]">
        <p className="flex min-w-0 flex-1 basis-60 items-center gap-2 text-[13px] text-muted-foreground">
          <Sparkles className="h-4 w-4 shrink-0" aria-hidden="true" />
          A IA segue o seu roteiro e monta os agentes no Touch Builder para você testar.
        </p>
        <PrimaryButton onClick={onBuild} disabled={!ready} className="px-6">
          Montar meus agentes
        </PrimaryButton>
      </div>
    </section>
  );
}
